/**
 * Embedding Providers Page - 임베딩 AI 제공자 연결
 *
 * 지식 파이프라인에서 사용할 임베딩 모델 제공자(Bedrock / OpenAI)를 연결합니다.
 * - Bedrock: Titan Embed V2, Cohere Embed V3 모두 제공
 * - OpenAI: API 키로 직접 연결
 * (시스템 설정 하위 메뉴)
 */

import { useState, useEffect } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Badge, Button, Modal, useToast } from '../components/ui';
import { 
  embeddingProviderApi, 
  EmbeddingAccount, 
  EmbeddingProviderStatus 
} from '../api/embeddingProvider';
import { authApi } from '../api';
import { isProductAdminRole, isTenantAdminRole } from '../utils/role';
import { EmbeddingAccountModal } from '../components/embedding/EmbeddingAccountModal';
import awsLogo from '../assets/icons/providers/aws.svg';
import openaiLogo from '../assets/icons/providers/openai.svg';
// Cohere 로고가 없어서 AWS 로고 사용 (Bedrock 통해 제공)
import '../styles/adminPage.css';

// 임베딩 제공자 타입
interface EmbeddingProvider {
  id: string;
  name: string;
  logo: string;
  description: string;
  models: string[];
  connected: boolean;
  apiKeyMasked?: string;
  inherited?: boolean;
  scope?: string;
}

// 초기 임베딩 제공자 목록
// - Bedrock: Titan Embed V2, Cohere Embed V3 모두 제공
// - OpenAI: API 키로 직접 연결
const DEFAULT_EMBEDDING_PROVIDERS: EmbeddingProvider[] = [
  {
    id: 'bedrock',
    name: 'Amazon Bedrock',
    logo: awsLogo,
    description: 'AWS 임베딩 모델 서비스 (Titan, Cohere)',
    models: ['Titan Embed V2', 'Cohere Embed V3'],
    connected: false,
  },
  {
    id: 'openai',
    name: 'OpenAI',
    logo: openaiLogo,
    description: 'OpenAI 임베딩 모델',
    models: ['text-embedding-3-small', 'text-embedding-3-large'],
    connected: false,
  },
];

// 로컬 스토리지 키
const STORAGE_KEY = 'admin_embedding_providers';

export function EmbeddingProvidersPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);
  const isTenantAdmin = isTenantAdminRole(currentUser?.role);
  const [providers, setProviders] = useState<EmbeddingProvider[]>(DEFAULT_EMBEDDING_PROVIDERS);

  // 설정 모달 상태
  const [configModal, setConfigModal] = useState<EmbeddingProvider | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Bedrock 멀티 어카운트
  const [bedrockAccounts, setBedrockAccounts] = useState<EmbeddingAccount[]>([]);
  const [showBedrockModal, setShowBedrockModal] = useState(false);

  useEffect(() => {
    loadProviders();
    loadBedrockAccounts();
  }, []);

  const loadBedrockAccounts = async () => {
    try {
      const accounts = await embeddingProviderApi.listAccounts();
      setBedrockAccounts(accounts);
    } catch { /* 로드 실패 무시 */ }
  };

  const [testingAccountId, setTestingAccountId] = useState<string | null>(null);

  const handleTestBedrockAccount = async (acc: EmbeddingAccount) => {
    setTestingAccountId(acc.id);
    const toastId = showToast('loading', '연결 테스트 중...', { title: acc.name });
    try {
      const r = await embeddingProviderApi.testAccount(acc.id);
      showToast('success', r.message || '연결이 정상입니다.', { id: toastId });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '연결 테스트 실패';
      showToast('error', '', { id: toastId, title: `${acc.name} 연결 실패`, detail: msg });
    } finally {
      setTestingAccountId(null);
    }
  };

  const handleDeleteBedrockAccount = async (acc: EmbeddingAccount) => {
    if (acc.inherited && !isProductAdmin && !isTenantAdmin) {
      showToast('info', '상위 스코프에서 등록한 계정은 여기서 삭제할 수 없습니다.');
      return;
    }
    if (!confirm(`"${acc.name}" 계정을 삭제하시겠습니까?`)) return;
    try {
      await embeddingProviderApi.deleteAccount(acc.id, acc.scope);
      setBedrockAccounts((prev) => prev.filter((a) => a.id !== acc.id));
      showToast('success', '계정이 삭제되었습니다.');
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    }
  };

  const [activatingAccountId, setActivatingAccountId] = useState<string | null>(null);

  const handleActivateBedrockAccount = async (acc: EmbeddingAccount) => {
    if (acc.isActive) return;
    if (acc.inherited) {
      showToast('info', '상위 스코프에서 등록한 계정은 활성화할 수 없습니다.');
      return;
    }
    setActivatingAccountId(acc.id);
    try {
      await embeddingProviderApi.activateAccount(acc.id);
      setBedrockAccounts((prev) =>
        prev.map((a) => ({ ...a, isActive: a.id === acc.id }))
      );
      showToast('success', `"${acc.name}" 계정이 활성화되었습니다.`);
    } catch {
      showToast('error', '활성화에 실패했습니다.');
    } finally {
      setActivatingAccountId(null);
    }
  };

  const loadProviders = async () => {
    let statuses: Record<string, EmbeddingProviderStatus> = {};
    try {
      statuses = await embeddingProviderApi.getAllProviderStatuses();
    } catch {
      // 로그인 전이거나 네트워크 오류 시 localStorage 폴백
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    let localParsed: Partial<EmbeddingProvider>[] = [];
    try { localParsed = stored ? JSON.parse(stored) : []; } catch { /* ignore */ }

    const merged = DEFAULT_EMBEDDING_PROVIDERS.map((def) => {
      if (def.id === 'bedrock') return def;
      const remote = statuses[def.id];
      if (remote) return { ...def, connected: remote.connected, apiKeyMasked: remote.apiKeyMasked, inherited: remote.inherited, scope: remote.scope };
      const local = localParsed.find((p) => p.id === def.id);
      return local ? { ...def, connected: !!local.connected, apiKeyMasked: local.apiKeyMasked } : def;
    });
    setProviders(merged);
  };

  const saveProviders = (updated: EmbeddingProvider[]) => {
    setProviders(updated);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(updated.map(({ id, connected, apiKeyMasked }) => ({ id, connected, apiKeyMasked }))),
    );
  };

  const openConfigModal = (provider: EmbeddingProvider) => {
    setConfigModal(provider);
    setApiKeyInput('');
  };

  const closeConfigModal = () => {
    setConfigModal(null);
    setApiKeyInput('');
  };

  const handleSaveApiKey = async () => {
    if (!configModal) return;

    if (configModal.id === 'bedrock') {
      showToast('info', 'Amazon Bedrock은 AWS 프로필을 통해 인증됩니다.');
      closeConfigModal();
      return;
    }

    if (!apiKeyInput.trim()) {
      if (configModal.connected) {
        closeConfigModal();
        return;
      }
      showToast('error', 'API Key를 입력해주세요.');
      return;
    }

    setSaving(true);
    try {
      const result = await embeddingProviderApi.saveApiKey(configModal.id, apiKeyInput.trim());
      const updated = providers.map((p) =>
        p.id === configModal.id
          ? { ...p, connected: true, apiKeyMasked: result.apiKeyMasked }
          : p
      );
      saveProviders(updated);
      showToast('success', `${configModal.name} 연결이 완료되었습니다.`);
      closeConfigModal();
    } catch (error) {
      showToast('error', '설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (provider: EmbeddingProvider) => {
    if (provider.id === 'bedrock') {
      showToast('info', 'Amazon Bedrock은 기본 연결이며 해제할 수 없습니다.');
      return;
    }
    try {
      await embeddingProviderApi.deleteApiKey(provider.id);
    } catch { /* 이미 없는 경우 무시 */ }
    const updated = providers.map((p) =>
      p.id === provider.id ? { ...p, connected: false, apiKeyMasked: undefined } : p
    );
    saveProviders(updated);
    showToast('success', `${provider.name} 연결이 해제되었습니다.`);
    closeConfigModal();
  };

  const [testingProviderId, setTestingProviderId] = useState<string | null>(null);

  const handleTestConnection = async (provider: EmbeddingProvider) => {
    if (testingProviderId) return;
    setTestingProviderId(provider.id);
    const toastId = showToast('loading', '연결 테스트 중...', { title: provider.name });
    try {
      const result = await embeddingProviderApi.testConnection(provider.id);
      showToast('success', result.message || `${provider.name} 연결이 정상입니다.`, { id: toastId });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error)?.message
        || '연결 테스트 실패';
      showToast('error', '', { id: toastId, title: `${provider.name} 연결 실패`, detail: msg });
    } finally {
      setTestingProviderId(null);
    }
  };

  const bedrockConnected = bedrockAccounts.length > 0;
  const isConnected = (p: EmbeddingProvider) => (p.id === 'bedrock' ? bedrockConnected : p.connected);
  const connectedCount = providers.filter(isConnected).length;

  return (
    <>
      {!embedded && (
        <Topbar title="임베딩 AI 제공자 연결" description="지식 파이프라인에서 사용할 임베딩 모델 제공자를 연결합니다" />
      )}

      <div className={embedded ? '' : 'page-content'}>
        <section>
          <div className={`admin-panel-head${embedded ? '' : ' admin-panel-head--compact'}`}>
            {embedded ? (
              <PanelTitle
                title="임베딩 AI 제공자 연결"
                description="지식 파이프라인에서 사용할 임베딩 모델 제공자를 연결합니다. 연결된 제공자의 모델만 지식베이스 생성 시 선택할 수 있습니다."
              />
            ) : <span />}
            <div className="provider-count">
              <b>{connectedCount}</b> / {providers.length} 연결됨
            </div>
          </div>

          <div className="provider-grid">
            {providers.map((provider) => (
              <div key={provider.id} className={`provider-card${isConnected(provider) ? ' connected' : ''}`}>
                <div className="provider-card-top">
                  <div className="provider-logo">
                    <img src={provider.logo} alt="" />
                  </div>
                  {isConnected(provider) ? (
                    provider.inherited
                      ? <Badge color="info">상위 상속</Badge>
                      : <Badge color="success">연결됨</Badge>
                  ) : (
                    <Badge color="gray">미연결</Badge>
                  )}
                </div>

                <div>
                  <div className="provider-name">{provider.name}</div>
                  <div className="provider-desc">{provider.description}</div>
                </div>

                <div className="choice-card-tags provider-models">
                  {provider.models.map((model) => (
                    <Badge key={model} variant="type">{model}</Badge>
                  ))}
                </div>

                {provider.connected && provider.inherited ? (
                  <div className="provider-key">
                    <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                      상위 테넌트에서 설정한 연결을 사용 중입니다.
                    </span>
                  </div>
                ) : provider.connected && provider.apiKeyMasked ? (
                  <div className="provider-key">
                    API Key
                    <code>{provider.apiKeyMasked}</code>
                  </div>
                ) : null}

                {provider.id === 'bedrock' ? (
                  bedrockConnected ? (
                    <>
                      <div className="bedrock-account-list">
                        {bedrockAccounts.map((acc) => (
                          <div key={acc.id} className={`bedrock-account-row${acc.isActive ? ' active' : ''}`}>
                            <label className="bedrock-account-radio" title={acc.inherited ? '상위 스코프 계정은 활성화할 수 없습니다' : '이 계정 활성화'}>
                              <input
                                type="radio"
                                name="embedding-bedrock-active-account"
                                checked={acc.isActive || false}
                                onChange={() => handleActivateBedrockAccount(acc)}
                                disabled={acc.inherited || activatingAccountId !== null}
                              />
                              <span className="bedrock-account-info">
                                <strong>{acc.name}</strong>
                                <span>{acc.authType === 'assumeRole' ? 'Assume Role' : 'Access Key'} · {acc.region}</span>
                              </span>
                              <span className="bedrock-account-actions">
                                <button className="bedrock-account-btn bedrock-account-btn-test" onClick={() => handleTestBedrockAccount(acc)}
                                  disabled={testingAccountId === acc.id} title="연결 테스트">
                                  {testingAccountId === acc.id ? '…' : '테스트'}
                                </button>
                                {(acc.inherited && !isProductAdmin && !isTenantAdmin) ? (
                                  <span style={{ fontSize: 11, color: 'var(--text-3)', padding: '0 6px' }}>상위에서 관리</span>
                                ) : (
                                  <button className="bedrock-account-btn bedrock-account-btn-del" onClick={() => handleDeleteBedrockAccount(acc)} title="삭제">계정 삭제</button>
                                )}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="provider-actions">
                        <Button color="gray" appearance="outline" onClick={() => setShowBedrockModal(true)}>
                          + 계정 추가
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="provider-actions">
                      <Button color="primary" onClick={() => setShowBedrockModal(true)}>
                        연결하기
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="provider-actions">
                    {provider.connected && !provider.inherited ? (
                      <>
                        <Button color="gray" appearance="outline" onClick={() => handleTestConnection(provider)} disabled={testingProviderId === provider.id}>
                          {testingProviderId === provider.id ? '테스트 중...' : '테스트'}
                        </Button>
                        <Button color="error" appearance="outline" onClick={() => handleDisconnect(provider)}>
                          연결해제
                        </Button>
                      </>
                    ) : (
                      <Button color="primary" onClick={() => openConfigModal(provider)}>
                        {provider.inherited ? '직접 연결' : '연결하기'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 제공자 연결 / 설정 모달 */}
      <Modal
        isOpen={!!configModal}
        onClose={closeConfigModal}
        title={configModal ? `${configModal.name} ${configModal.connected ? '설정' : '연결'}` : ''}
        size="sm"
        footerLeft={
          configModal?.connected && configModal.id !== 'bedrock' ? (
            <Button color="error" appearance="outline" onClick={() => handleDisconnect(configModal)}>
              연결 해제
            </Button>
          ) : undefined
        }
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeConfigModal}>
              {configModal?.id === 'bedrock' ? '닫기' : '취소'}
            </Button>
            {configModal && configModal.id !== 'bedrock' && (
              <Button color="primary" onClick={handleSaveApiKey} disabled={saving}>
                {saving ? '저장 중...' : configModal.connected ? '저장' : '연결'}
              </Button>
            )}
          </>
        }
      >
        {configModal?.id === 'bedrock' ? (
          <div className="config-info">
            <p>Amazon Bedrock은 AWS 프로필을 통해 인증됩니다.</p>
            <p>현재 프로필: <strong>lab-on</strong></p>
            <p className="config-hint">
              다른 프로필을 사용하려면 백엔드 환경 변수를 수정하세요.
            </p>
          </div>
        ) : configModal && (
          <>
            <label className="provider-key-label" htmlFor="embedding-provider-api-key">API Key</label>
            <div className="input">
              <input
                id="embedding-provider-api-key"
                type="password"
                className="input-control"
                placeholder={
                  configModal.connected
                    ? `현재 ${configModal.apiKeyMasked ?? ''} · 변경할 때만 입력`
                    : `${configModal.name} API Key를 입력하세요`
                }
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                autoFocus
              />
            </div>
            <p className="config-hint">
              API Key는 암호화되어 안전하게 저장됩니다.
              {configModal.id === 'openai' && (
                <> <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">OpenAI API Keys</a>에서 발급받을 수 있습니다.</>
              )}
            </p>
          </>
        )}
      </Modal>

      {/* Bedrock 계정 등록 마법사 */}
      <EmbeddingAccountModal
        isOpen={showBedrockModal}
        onClose={() => setShowBedrockModal(false)}
        onSaved={loadBedrockAccounts}
      />
    </>
  );
}

export default EmbeddingProvidersPage;
